/**
 * MyAgent API Service Factory
 * Platform-agnostic API wrappers for MyAgent RPC methods.
 *
 * Chat is local-only: each bot config carries its own provider + apiKey
 * (LlmBotEditor enforces this). Sessions/storage go through the local
 * sandbox under the worker name 'local'; chat routes through llm.proxy
 * (web) or direct frontend SDK (mobile).
 */

import { isAuthError } from 'workers-common'

export function createApiService({
  workerName,
  getClient,
  onAuthError,
  getFrontendLlmSdk,
  platform = 'web',
}: any) {
  const callWithAuth = async (target: string, method: string, params: any[], opts?: any) => {
    const client = getClient()
    try {
      return await client.callByNameWithAuth(target, method, params, opts)
    } catch (error: any) {
      if (isAuthError(error) && onAuthError) {
        console.warn('[API] Authentication error detected:', error.message)
        onAuthError(error)
      }
      throw error
    }
  }

  const callWorker = (method: string, params: any[], opts?: any) =>
    callWithAuth(workerName, method, params, opts)

  const callSession = (method: string, params: any[]) => callWithAuth(workerName, method, params)

  const call = async (method: string, params: any[]) => {
    const client = getClient()
    return client.callByName(workerName, method, params)
  }

  return {
    // System APIs
    healthCheck: () => call('system.health', []),
    getAvailableTools: () => call('system.getAvailableTools', []),
    getAvailableModels: () => call('system.getAvailableModels', []),

    // --- Session APIs (unified, per-conversation) ---

    getConversationIndex: () => callSession('session.getConversationIndex', [{}]),

    createConversation: (params: any) => callSession('session.createConversation', [params]),

    renameConversation: (conversationId: string, title: string) =>
      callSession('session.renameConversation', [{ conversationId, title }]),

    deleteConversation: (conversationId: string) =>
      callSession('session.deleteConversation', [{ conversationId }]),

    getMessages: (conversationId: string, maxMessages?: number) =>
      callSession('session.getMessages', [{ conversationId, maxMessages }]),

    getMessagesForLLM: (conversationId: string, nameMap: any) =>
      callSession('session.getMessagesForLLM', [{ conversationId, nameMap }]),

    saveMessages: (conversationId: string, messages: any[]) =>
      callSession('session.saveMessages', [{ conversationId, messages }]),

    getParticipants: (conversationId: string) =>
      callSession('session.getParticipants', [{ conversationId }]),

    setParticipants: (conversationId: string, participantRoleIds: string[]) =>
      callSession('session.setParticipants', [{ conversationId, participantRoleIds }]),

    clearSession: () => callSession('session.clear', [{}]),

    // --- Chat (local LLM call via user-supplied API key) ---

    chat: async (params: any, botConfig: any = null) => {
      const { provider, apiKey, model } = botConfig?.llm || {}

      if (!provider || (!apiKey && provider !== 'ollama')) {
        throw new Error('Bot is missing provider or API key. Edit the bot and fill them in.')
      }

      if (platform === 'mobile' && getFrontendLlmSdk) {
        // llm-sdk chat() returns { message: { content, ... }, usage, raw }
        // chat-engine (peer-sdk) expects { answer, usage } — flatten to match contract
        const res = await getFrontendLlmSdk().chat({
          provider,
          apiKey,
          model,
          messages: params.messages,
        })
        return { answer: res?.message?.content, usage: res?.usage }
      }

      return callWorker('llm.proxy', [
        {
          provider,
          apiKey,
          model,
          messages: params.messages,
        },
      ])
    },
  }
}
